import useSWR from 'swr';
import { ErrorResponse, UpdateAddressInfo } from 'types/form';
import { setLocalStorage } from 'utils/setLocalStorage';
import { ApiResources, createErrorResponse, getContactData, getRequestHeaders, getUrl, Methods, processResponse } from './utils';

interface IAddress {
  isCurrent: boolean;
  includeInDti: boolean;
  monthsAtAddress: string | null;
  streetLine1: string;
  streetLine2: string | null;
  city: string;
  state: string;
  zip: string;
  type: string | null;
  county: string | null;
  unit: string;
  monthlyPayment: number;
}

interface IContactDetailsResponse {
  firstName: string;
  lastName: string;
  addresses: IAddress[];
  ssn: string;
  dateOfBirth: string;
  cellPhone: string;
  email: string;
}

interface IMetadataContactDetails {
  contactId: string;
  leadId: string;
  assetId: string | null;
}

export interface IContactDetailsFromMetadataResponse {
  contactId: string | null;
  leadId: string | null;
  assetId: string | null;
  leadSource: string | null;
  contact: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    ssn: string | null;
    cellPhone: string | null;
    dateOfBirth: string | null;
    residencyStatusType: string | null;
    addresses: UpdateAddressInfo[] | null;
  } | null;
  asset: {
    vin: string | null;
    year: number | null;
    make: string | null;
    model: string | null;
    mileage: number | null;
    assetLienHolder: {
      name: string | null;
      lienHolderId: string | null;
      loan: {
        rate: number | null;
        term: number | null;
        loanAmount: number | null;
        monthlyPayment: number | null;
      } | null;
    } | null;
  } | null;
  error: null;
  isSuccess: boolean | null;
}

export const getContactDetailsByContactId = async (contactId: string): Promise<IContactDetailsResponse | ErrorResponse> => {
  try {
    const getContactByIdEndpoint = `${getUrl(ApiResources.getContactByContactId)}/${contactId}`;
    const request = {
      method: Methods.get,
      headers: await getRequestHeaders(),
    };

    const contactDetailsResponse = await fetch(getContactByIdEndpoint, request);

    return processResponse(contactDetailsResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const usefetchContactDetailsByContactId = (id: string | null) => {
  const swrKey = id ? ['contactDetailsByContactId', id] : null;

  return useSWR(
    swrKey,
    async () => {
      if (!id) {
        throw new Error('Contact id are required');
      }
      const response = await getContactDetailsByContactId(id as string);
      if ('error' in response) {
        throw new Error(response?.error?.message);
      }
      const contactDetails = getContactData();
      const contactData = {
        ...contactDetails,
        addresses: response?.addresses,
      };
      setLocalStorage(contactData, 'contactData');

      return response;
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    }
  );
};

export const getContactDetailsFromSourceContactId = async (metadata: IMetadataContactDetails, signal?: AbortSignal): Promise<IContactDetailsFromMetadataResponse | ErrorResponse> => {
  try {
    const getContactDetailsEndpoint = `${getUrl(ApiResources.getContactByContactId)}/contact`;
    const request = {
      method: Methods.post,
      headers: await getRequestHeaders(),
      body: JSON.stringify(metadata),
      signal
    };
    const contactDetailsFromMetadataResponse = await fetch(getContactDetailsEndpoint, request);
    return processResponse(contactDetailsFromMetadataResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};